import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import BankTable from './BankTable'
import Pagination from './Pagination'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiDatabase, FiSearch, FiFilter, FiTrendingUp, FiUsers, FiTarget } = FiIcons

const ResultsSection = () => {
  const { 
    currentLang, 
    totalCount, 
    startIndex, 
    endIndex, 
    isSearching,
    searchQuery,
    translations 
  } = useApp()

  // 構建結果計數文字
  const getResultsCountText = () => {
    if (totalCount === 0) {
      return currentLang === 'zh' ? '沒有找到結果' : 'No results found'
    }
    
    if (currentLang === 'zh') {
      return `第 ${startIndex}-${endIndex} 項，共 ${totalCount} 間銀行`
    } else {
      return `Showing ${startIndex}-${endIndex} of ${totalCount} banks`
    }
  }

  // Mobile results count - more compact
  const getMobileResultsCountText = () => {
    if (totalCount === 0) {
      return currentLang === 'zh' ? '無結果' : 'No results'
    }
    
    if (currentLang === 'zh') {
      return `${startIndex}-${endIndex} / ${totalCount}`
    } else {
      return `${startIndex}-${endIndex} of ${totalCount}`
    }
  }

  // 判斷是否為少量結果（需要特殊佈局處理）- 調整為5項以下
  const isLowResultCount = totalCount > 0 && totalCount <= 5

  return (
    <motion.section
      id="results-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
    >
      {/* Results Header - Mobile Optimized */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/70 to-purple-50/30 dark:from-gray-700/70 dark:to-purple-900/20">
        {/* Mobile Layout */}
        <div className="flex items-center justify-between sm:hidden">
          <div className="flex items-center gap-2">
            <motion.div 
              className="w-6 h-6 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiDatabase} className="w-3 h-3 text-white" />
            </motion.div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {currentLang === 'zh' ? '搜尋結果' : 'Results'}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {getMobileResultsCountText()}
              </p>
            </div>
          </div>
          
          {searchQuery && (
            <motion.div 
              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded-md border border-purple-200 dark:border-purple-700"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <SafeIcon icon={FiSearch} className="w-3 h-3" />
              <span className="truncate max-w-[80px]">"{searchQuery}"</span>
            </motion.div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-md"
              whileHover={{ scale: 1.1, rotate: 10 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiDatabase} className="w-4 h-4 text-white" />
            </motion.div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {translations.resultsTitle[currentLang]}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getResultsCountText()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {searchQuery && (
              <motion.div 
                className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
              >
                <SafeIcon icon={FiSearch} className="w-4 h-4" />
                <span>"{searchQuery}"</span>
              </motion.div>
            )}
            
            {/* Enhanced Statistics */}
            {totalCount > 0 && (
              <motion.div 
                className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-700 shadow-sm"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                <span>{totalCount} {currentLang === 'zh' ? '個結果' : 'results'}</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Results Content */}
      {isSearching ? (
        <div className="p-6 sm:p-8 text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-3 sm:mb-4"
          />
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
            {translations.searching[currentLang]}
          </p>
        </div>
      ) : totalCount === 0 ? (
        <motion.div 
          className="p-6 sm:p-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <SafeIcon icon={FiSearch} className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </motion.div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
            {translations.noResults?.title?.[currentLang] || (currentLang === 'zh' ? '沒有找到結果' : 'No Results Found')}
          </h3>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
            {translations.noResults?.message?.[currentLang] || (currentLang === 'zh' ? '請嘗試調整搜尋條件或清除篩選器' : 'Try adjusting your search terms or clear filters')}
          </p>
        </motion.div>
      ) : (
        <div className={isLowResultCount ? 'min-h-[450px] flex flex-col' : ''}>
          {/* 少量結果時的特殊處理 - 改進設計 */}
          {isLowResultCount && (
            <motion.div 
              className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-blue-200 dark:border-blue-800"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-3 text-sm">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.2 }}
                >
                  <SafeIcon icon={FiTarget} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </motion.div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  {currentLang === 'zh' 
                    ? `精確匹配：找到 ${totalCount} 個結果` 
                    : `Precise match: Found ${totalCount} result${totalCount > 1 ? 's' : ''}`
                  }
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-blue-200 to-transparent dark:from-blue-700 dark:to-transparent"></div>
              </div>
            </motion.div>
          )}
          
          <div className={isLowResultCount ? 'flex-1 flex flex-col justify-center py-6' : ''}>
            <BankTable />
          </div>
          
          {/* 智能分頁顯示 - 只有在結果超過每頁顯示數量時才顯示分頁 */}
          {totalCount > 10 && <Pagination />}
        </div>
      )}
    </motion.section>
  )
}

export default ResultsSection